import './AddPagesDialog.scss';
import React, { useEffect, useState } from 'react';
import SimpleBar from 'simplebar-react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { useTranslation } from 'react-i18next';

interface AddPagesDialogProps {
  visible: boolean;
  onHide: () => void;
  onSave: (urls: string[]) => void;
  isSaving: boolean;
}

const AddPagesDialog: React.FC<AddPagesDialogProps> = ({ visible, onHide, onSave, isSaving }) => {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>(['']);

  useEffect(() => {
    if (visible) setUrls(['']);
  }, [visible]);

  const addUrl = () => setUrls([...urls, '']);

  const removeUrl = (index: number) => setUrls(urls.filter((_, i) => i !== index));

  const updateUrl = (index: number, value: string) => {
    const updated = [...urls];
    updated[index] = value;
    setUrls(updated);
  };

  const normalizeUrl = (url: string): string => {
    const trimmed = url.trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
  };

  const handleSave = () => {
    const validUrls = urls.map(normalizeUrl).filter(Boolean);
    if (validUrls.length === 0) return;
    onSave(validUrls);
    onHide();
  };

  const hasValidUrl = urls.some((u) => u.trim().length > 0);

  const footer = (
    <Button
      className="btn btn--sm btn--primary"
      label={t('save')}
      onClick={handleSave}
      disabled={!hasValidUrl || isSaving}
    />
  );

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={t('addPages')}
      footer={footer}
      className="dialog-box add-pages-dialog"
      draggable={false}
      style={{ width: '520px' }}
    >
      <div className="add-pages-dialog__content">
        <SimpleBar style={{ maxHeight: '300px' }} autoHide={false}>
          <ul className="add-pages-dialog__list">
            {urls.map((url, index) => (
              <li key={index}>
                <div className="add-pages-dialog__list-block">
                  <InputText
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                    placeholder="example.com/page"
                  />
                  {urls.length > 1 && (
                    <Button
                      className="add-pages-dialog__remove-btn"
                      onClick={() => removeUrl(index)}
                      type="button"
                    >
                      <span className="ico--trash ico--xs" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </SimpleBar>

        <div className="add-pages-dialog__add-new">
          <div className="add-pages-dialog__list-block">
            <label />
            <Button
              className="btn btn--link btn--link-black add-pages-dialog__add-btn"
              onClick={addUrl}
              type="button"
            >
              {t('addAnotherPage')}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddPagesDialog;
